<template>
  <div>
  <div class="datatable-icons">
    <font-awesome-icon :icon="icon.angleDownIcon" class="datatable-icons__angledown" />
    <font-awesome-icon :icon="icon.angleUpIcon" class="datatable-icons__angleup" />
    <font-awesome-icon :icon="icon.searchIcon" class="datatable-icons__search" />
  </div>
    <div class="table-component">
        <div v-if="showFilter && filterableColumnExists" class="table-component__filter">
            <input
                    :class="fullFilterInputClass"
                    type="text"
                    v-model="filter"
                    :placeholder="filterPlaceholder"
            >
            <a
                    v-if="filter"
                    @click="filter = ''"
                    class="table-component__filter__clear"
            >×</a>
        </div>

        <div class="table-component__table-wrapper">
            <table :class="fullTableClass">
                <caption v-if="showCaption" class="table-component__table__caption" role="alert" aria-live="polite">
                    {{ ariaCaption }}
                </caption>
                <thead :class="fullTableHeadClass">
                <tr>
                    <table-column-header
                            @click="changeSorting"
                            v-for="column in columns"
                            :key="column.show"
                            :sort="sort"
                            :column="column"
                    ></table-column-header>
                </tr>
                </thead>
                <tbody :class="fullTableBodyClass">
                <table-row
                  v-for="row in displayedRows"
                  :key="row.vueTableComponentInternalRowId"
                  :row="row"
                  :columns="columns"
                  @rowClick="emitRowClick"/>
                </tbody>
                <tfoot>
                    <slot name="tfoot" :rows="rows"></slot>
                </tfoot>
            </table>
        </div>

        <div v-if="displayedRows.length === 0" class="table-component__message">
            {{ filterNoResults }}
        </div>

        <div style="display:none;">
            <slot></slot>
        </div>

        <pagination v-if="pagination" :pagination="pagination" @pageChange="pageChange"></pagination>
    </div>
  </div>
</template>

<script>
import Column from '../classes/Column';
import Row from '../classes/Row';
import TableColumnHeader from './TableColumnHeader';
import TableRow from './TableRow';
import Pagination from './Pagination';
import { classList } from '../helpers';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';

export default {
  components: {
    TableColumnHeader,
    TableRow,
    Pagination,
    FontAwesomeIcon
  },

  props: {
    data: { default: () => [], type: [Array, Function] },

    showFilter: { default: true },
    showCaption: { default: false },

    sortBy: { default: '', type: String },
    sortOrder: { default: '', type: String },

    cacheKey: { default: null },
    cacheLifetime: { default: 5 },

    tableClass: { default: () => '' },
    theadClass: { default: () => '' },
    tbodyClass: { default: () => '' },
    filterInputClass: { default: () => '' },
    filterPlaceholder: { default: () => '' },
    filterNoResults: { default: () => '' }
  },

  data: () => ({
    icon: {
      angleDownIcon: faAngleDown,
      angleUpIcon: faAngleUp,
      searchIcon: faSearch
    },
    columns: [],
    rows: [],
    filter: '',
    sort: {
      fieldName: '',
      order: ''
    },
    pagination: null,

    localSettings: {}
  }),

  created () {
    this.sort.fieldName = this.sortBy;
    this.sort.order = this.sortOrder;
  },

  async mounted () {
    this.$nextTick(function () {
      // Adds search icon
      const searchIcon = document.querySelector('.datatable-icons__search');
      const filter = document.querySelector('.table-component__filter');
      if (filter) {
        filter.appendChild(searchIcon);
      }

      // Adds Up/Down arrows
      const upIcon = document.querySelector('.datatable-icons__angleup');
      const downIcon = document.querySelector('.datatable-icons__angledown');
      document
        .querySelectorAll("[class*='table-component__th--sort']")
        .forEach(function (el) {
          const upIconClone = upIcon.cloneNode(true);
          const downIconClone = downIcon.cloneNode(true);
          el.appendChild(upIconClone);
          el.appendChild(downIconClone);
        });
    });

    const columnComponents = this.$slots.default
      .filter(column => column.componentInstance)
      .map(column => column.componentInstance);

    this.columns = columnComponents.map(
      column => new Column(column)
    );

    columnComponents.forEach(columnCom => {
      Object.keys(columnCom.$options.props).forEach(
        prop => columnCom.$watch(prop, () => {
          this.columns = columnComponents.map(
            column => new Column(column)
          );
        })
      );
    });

    await this.mapDataToRows();
  },

  watch: {
    filter () {
      if (!this.usesLocalData) {
        this.mapDataToRows();
      }
    },

    data () {
      if (this.usesLocalData) {
        this.mapDataToRows();
      }
    }
  },

  computed: {
    fullTableClass () {
      return classList('table-component__table', this.tableClass);
    },

    fullTableHeadClass () {
      return classList('table-component__table__head', this.theadClass);
    },

    fullTableBodyClass () {
      return classList('table-component__table__body', this.tbodyClass);
    },

    fullFilterInputClass () {
      return classList('table-component__filter__field', this.filterInputClass);
    },

    ariaCaption () {
      if (this.sort.fieldName === '') {
        return 'Table not sorted';
      }

      return `Table sorted by ${this.sort.fieldName} ` +
                    (this.sort.order === 'asc' ? '(ascending)' : '(descending)');
    },

    usesLocalData () {
      return Array.isArray(this.data);
    },

    displayedRows () {
      if (!this.usesLocalData) {
        return this.sortedRows;
      }

      if (!this.showFilter) {
        return this.sortedRows;
      }

      if (!this.columns.filter(column => column.isFilterable()).length) {
        return this.sortedRows;
      }

      return this.sortedRows.filter(row => row.passesFilter(this.filter));
    },

    sortedRows () {
      if (!this.usesLocalData) {
        return this.rows;
      }

      if (this.sort.fieldName === '') {
        return this.rows;
      }

      if (this.columns.length === 0) {
        return this.rows;
      }

      const sortColumn = this.getColumn(this.sort.fieldName);

      if (!sortColumn) {
        return this.rows;
      }

      return this.rows.slice().sort(sortColumn.getSortPredicate(this.sort.order, this.columns));
    },

    filterableColumnExists () {
      return this.columns.filter(c => c.isFilterable()).length > 0;
    },

    storageKey () {
      return this.cacheKey
        ? `vue-table-component.${this.cacheKey}`
        : `vue-table-component.${window.location.host}${window.location.pathname}${this.cacheKey}`;
    }
  },

  methods: {
    async pageChange (page) {
      this.pagination.currentPage = page;

      await this.mapDataToRows();
    },

    async mapDataToRows () {
      const data = this.usesLocalData
        ? this.prepareLocalData()
        : await this.fetchServerData();

      let rowId = 0;

      this.rows = data
        .map(rowData => {
          rowData.vueTableComponentInternalRowId = rowId++;
          return rowData;
        })
        .map(rowData => new Row(rowData, this.columns));
    },

    prepareLocalData () {
      this.pagination = null;

      return this.data;
    },

    async fetchServerData () {
      const page = this.pagination && (this.pagination.currentPage || 1);

      const response = await this.data({
        filter: this.filter,
        sort: this.sort,
        page: page
      });

      this.pagination = response.pagination;

      return response.data;
    },

    async refresh () {
      await this.mapDataToRows();
    },

    changeSorting (column) {
      if (this.sort.fieldName !== column.show) {
        this.sort.fieldName = column.show;
        this.sort.order = 'asc';
      } else {
        this.sort.order = (this.sort.order === 'asc' ? 'desc' : 'asc');
      }

      if (!this.usesLocalData) {
        this.mapDataToRows();
      }
    },

    getColumn (columnName) {
      return this.columns.find(column => column.show === columnName);
    },

    emitRowClick (row) {
      this.$emit('rowClick', row);
      this.$emit('row-click', row);
    }
  }
};
</script>

<style lang="scss" scoped>
  @import "../../../../stylesheets/variables.scss";

  .table-iconset-icon {
    margin: 0 0.5em;
    cursor: pointer;
  }

  .table-icon-disabled {
    opacity: 0.4;
    margin: 0 0.5em;
    cursor: default;
  }

  .datatable {
    &-icons {
      display: none;
    }
    &-icons__search {
      position: absolute;
      left: 0.6rem;
      top: 0.5rem;
      pointer-events: none;
    }
  }

  .table-component {
    $filter-padding: $sp-margin-smaller;

    thead.is-fixed {
      position: fixed;
      top: 0;
      left: 0;
      background-color: $white;
      &:before {
        content: "";
        position: fixed;
        display: block;
        width: $sp-margin;
        height: 100%;
        background-color: $white;
        left: 0;
        top: 0;
        z-index: 1;
      }
      &:after {
        content: "";
        position: fixed;
        display: block;
        width: $sp-margin;
        height: 100%;
        background-color: $white;
        right: 0;
        top: 0;
        z-index: 1;
      }
    }

    &__th--sort,
    &__th--sort-asc,
    &__th--sort-desc {
      cursor: pointer;
      position: relative;
      padding-right: 1rem !important;
      &::before,
      &::after {
        position: absolute;
        font-size: 0.3rem;
        right: 0.3rem;
        opacity: 0.5;
      }
      &:before {
        top: 0.7rem;
      }
      &:after {
        bottom: 0.6rem;
      }
    }
    &__th--sort,
    &__th--sort-asc,
    &__th--sort-desc {
      position: relative;
      padding-right: 1rem;
      .datatable-icons__angleup {
        position: absolute;
        top: 0.45rem;
        right: 0.3rem;
        width: 0.4rem;
      }
      .datatable-icons__angledown {
        position: absolute;
        bottom: 0.45rem;
        right: 0.3rem;
        width: 0.4rem;
      }
    }

    &__th--sort-asc {
      .datatable-icons__angledown {
        display: none;
      }
      .datatable-icons__angleup {
        top: 0.6rem;
      }
    }
    &__th--sort-desc {
      .datatable-icons__angleup {
        display: none;
      }
      .datatable-icons__angledown {
        bottom: 0.6rem;
      }
    }
    &__danger {
      color: $error;
    }
    &__warning {
      color: $warning;
    }
    &__success {
      color: $success;
    }
    &__small {
      width: 100px;
    }
    &__quality-long {
      font-size: $fs-xs;
    }
    &__filter {
      position: relative;
      @media (min-width: $breakpoint) {
        float: right;
      }
      input {
        padding-left: 2rem;
      }
    }
    &__filter__field {
      border: 1px solid $black-trans6;
      border-radius: 2em;
      padding: $filter-padding;
    }
    &__minimum__width__column {
      width: $sp;
    }
    &__small__width__column {
      min-width: 9em;
    }
    &__wider__width__column {
      min-width: 15em;
    }
    &__extra__wide__width__column {
      min-width: 20em;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/shared/table/components/TableComponent.vue